import type { DurationValue } from "components/ds/DurationField";

// How the server judges a requested duration against a cap: it shifts the
// calendar from now by each (a month from 31 January ends on 28 February) and
// accepts the request when it ends no later than the cap, give or take 3
// seconds. The client can't know which day the server will shift from, so
// where the two durations are in units of different lengths it compares the
// longest the request can be against the shortest the cap can be.

const FIXED_SECONDS: Record<string, number> = {
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800,
};

const MONTHS_IN: Record<string, number> = { month: 1, year: 12 };

const DAY = 86400;
const LONGEST: Record<string, number> = { month: 31 * DAY, year: 366 * DAY };
const SHORTEST: Record<string, number> = { month: 28 * DAY, year: 365 * DAY };

export type UnitAmount = { num: number; unit: string };

export const baseUnit = (unit: string): string => unit.replace(/s$/, "");

const seconds = (
  { num, unit }: UnitAmount,
  calendar: Record<string, number>,
): number => {
  const base = baseUnit(unit);
  return num * (FIXED_SECONDS[base] ?? calendar[base] ?? 0);
};

export const longestSeconds = (d: UnitAmount): number => seconds(d, LONGEST);

const shortestSeconds = (d: UnitAmount): number => seconds(d, SHORTEST);

// Whether the server may refuse `requested` under `cap`, whatever day it is.
export const exceedsCap = (requested: UnitAmount, cap: UnitAmount): boolean => {
  const requestedMonths = MONTHS_IN[baseUnit(requested.unit)];
  const capMonths = MONTHS_IN[baseUnit(cap.unit)];
  if (requestedMonths && capMonths)
    return requested.num * requestedMonths > cap.num * capMonths;
  return longestSeconds(requested) > shortestSeconds(cap);
};

const PICKER_UNIT: Record<string, DurationValue["unit"]> = {
  minute: "minutes",
  hour: "hours",
  day: "days",
  week: "weeks",
  month: "months",
};

// A cap as a duration a picker can hold, in the cap's own unit, or null when
// there is none. Years become months, which the server shifts to the same day.
export const capDuration = (
  value: number | null | undefined,
  unit: string | null | undefined,
): DurationValue | null => {
  if (!unit || typeof value !== "number" || value < 1) return null;
  const base = baseUnit(unit);
  if (base in PICKER_UNIT) return { num: value, unit: PICKER_UNIT[base] };
  if (base === "year") return { num: value * 12, unit: "months" };
  return null;
};
